/**
 * use-platform-feedback.ts — Hooks cho Platform → Góp ý của khách (super-admin).
 * Backend: ecdp_backend_source/src/modules/platform/platform-feedback-routes.ts
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type { FeedbackItem, FeedbackList, FeedbackStatus } from '@/pages/platform/feedback-helpers'

const KEY = ['platform', 'feedback'] as const

export interface FeedbackFilters { status?: FeedbackStatus | ''; type?: string; q?: string; page: number; pageSize: number }

export function useFeedbackList(f: FeedbackFilters) {
  return useQuery<FeedbackList>({
    queryKey: [...KEY, 'list', f],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await platformApi.get<FeedbackList>('/platform/feedback', {
        params: { status: f.status || undefined, type: f.type || undefined, q: f.q || undefined, page: f.page, pageSize: f.pageSize },
      })
      return data
    },
  })
}

/** Số góp ý MỚI — chấm đỏ ở menu Platform. Hỏi lại mỗi phút và khi quay lại tab; lỗi mạng thì im lặng (không làm phiền). */
export function useFeedbackUnread(enabled = true) {
  return useQuery<number>({
    queryKey: [...KEY, 'unread'],
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    queryFn: async () => (await platformApi.get<{ count: number }>('/platform/feedback/unread-count')).data.count,
  })
}

export function useUpdateFeedback() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status, adminNote }: { id: string; status?: FeedbackStatus; adminNote?: string }) =>
      (await platformApi.patch<FeedbackItem>(`/platform/feedback/${id}`, { status, adminNote })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

/** Ảnh đính kèm: route cần token Platform nên tải dạng blob (thẻ <img> trần không gắn được Authorization). */
export function useFeedbackImage(id: string, name: string) {
  return useQuery<Blob>({
    queryKey: [...KEY, 'image', id, name],
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
    queryFn: async () => (await platformApi.get<Blob>(`/platform/feedback/${id}/images/${name}`, { responseType: 'blob' })).data,
  })
}

// ── Thông báo Lark khi có góp ý mới (Platform → Góp ý → "Thông báo Lark") ──
// Backend: platform/platform-feedback-lark-routes.ts. URL bot / khoá ký không bao giờ được trả lại nguyên văn.
export interface LarkSettings {
  /** Đang thực sự báo vào Lark (đã bật + có URL hợp lệ). */
  active: boolean
  source: 'settings' | 'env' | 'none'
  enabled: boolean
  hasUrl: boolean
  urlMasked: string | null
  hasSecret: boolean
  envConfigured: boolean
  updatedAt: string | null
}
export interface LarkSettingsPatch { enabled?: boolean; webhookUrl?: string; secret?: string; clearSecret?: boolean; clear?: boolean }

const LARK_KEY = ['platform', 'feedback-lark'] as const
const LARK_URL = '/platform/settings/feedback-lark'

export function useLarkSettings(enabled = true) {
  return useQuery<LarkSettings>({
    queryKey: LARK_KEY,
    enabled,
    staleTime: 0,
    queryFn: async () => (await platformApi.get<LarkSettings>(LARK_URL)).data,
  })
}

export function useSaveLarkSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: LarkSettingsPatch) => (await platformApi.put<LarkSettings>(LARK_URL, patch)).data,
    onSuccess: (data) => qc.setQueryData(LARK_KEY, data),
  })
}

export function useTestLark() {
  return useMutation({
    mutationFn: async (body: { webhookUrl?: string; secret?: string }) =>
      (await platformApi.post<{ ok: boolean; error?: string }>(`${LARK_URL}/test`, body)).data,
  })
}

/** Tên tệp xuất: gop-y-<lọc>-YYYYMMDD-HHmm.json. Hàm thuần, có test. */
export function exportFileName(f: { status?: string; type?: string }, now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`
  const tag = [f.status, f.type].filter(Boolean).join('-')
  return `gop-y${tag ? `-${tag}` : ''}-${stamp}.json`
}

/**
 * Xuất JSON theo đúng bộ lọc đang xem (GET /platform/feedback/export) rồi tải về máy — mang file đi xử lý (vd đưa cho AI đọc).
 * Ảnh trong file là link ký sẵn, mở được không cần đăng nhập trong 7 ngày. Trả số góp ý đã xuất.
 */
export async function downloadFeedbackExport(f: Omit<FeedbackFilters, 'page' | 'pageSize'>): Promise<number> {
  const params = { status: f.status || undefined, type: f.type || undefined, q: f.q || undefined }
  const { data } = await platformApi.get<{ count: number }>('/platform/feedback/export', { params })
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = exportFileName(params)
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return data.count
}
